import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useNavigation } from '@/contexts/NavigationContext';

interface UsePermissionsOptions {
  projectId?: string;
  resourceOwnerId?: string;
}

interface Permissions {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  role: string | null;
  isAdmin: boolean;
  isLoading: boolean;
}

export const usePermissions = (options: UsePermissionsOptions = {}): Permissions => {
  const { user } = useAuth();
  const { profile, isAdmin, isLoading: profileLoading } = useProfile();
  const { navigation } = useNavigation();

  const projectId = options.projectId ?? navigation.projectId;
  const resourceOwnerId = options.resourceOwnerId;
  const isPersonal = navigation.isPersonal;

  const { data: projectRole, isLoading: roleLoading } = useQuery<string | null>({
    queryKey: ['user-project-role', projectId, user?.id],
    enabled: !!projectId && !!user,
    queryFn: async () => {
      // Check if owner first
      const { data: project } = await supabase
        .from('projects')
        .select('owner_id')
        .eq('id', projectId!)
        .single();

      if (project?.owner_id === user!.id) return 'owner';

      // Check project_members
      const { data: member } = await supabase
        .from('project_members')
        .select('role')
        .eq('project_id', projectId!)
        .eq('user_id', user!.id)
        .maybeSingle();

      return member?.role ?? null;
    },
  });

  const isLoading = profileLoading || (!!projectId && roleLoading);

  const permissions = useMemo<Omit<Permissions, 'isLoading'>>(() => {
    const isOwnerOfResource = !!resourceOwnerId && resourceOwnerId === user?.id;

    // Personal space
    if (isPersonal) {
      return {
        canCreate: true,
        canEdit: isOwnerOfResource,
        canDelete: isOwnerOfResource,
        role: 'owner',
        isAdmin,
      };
    }

    // Project context
    if (projectId) {
      const role = projectRole ?? null;

      if (role === 'owner') {
        return { canCreate: true, canEdit: true, canDelete: true, role, isAdmin };
      }
      if (role === 'editor') {
        return {
          canCreate: true,
          canEdit: true,
          canDelete: isOwnerOfResource,
          role,
          isAdmin,
        };
      }
      if (role === 'viewer') {
        return { canCreate: false, canEdit: false, canDelete: false, role, isAdmin };
      }

      // null role (global scope project, no explicit membership)
      return {
        canCreate: isAdmin,
        canEdit: isOwnerOfResource,
        canDelete: isOwnerOfResource,
        role: null,
        isAdmin,
      };
    }

    // Global context (no project, not personal)
    return {
      canCreate: isAdmin,
      canEdit: isOwnerOfResource,
      canDelete: isOwnerOfResource,
      role: null,
      isAdmin,
    };
  }, [isPersonal, projectId, projectRole, resourceOwnerId, user?.id, isAdmin]);

  return { ...permissions, isLoading };
};

export const useResourcePermissions = (resourceOwnerId: string, projectId?: string) => {
  return usePermissions({ resourceOwnerId, projectId });
};

export const useDirectResourcePermission = (type: 'prompt' | 'agent', resourceId?: string) => {
  const { user } = useAuth();
  const table = type === 'prompt' ? 'prompt_permissions' : 'agent_permissions';
  const resourceColumn = type === 'prompt' ? 'prompt_id' : 'agent_id';

  const { data: role, isLoading } = useQuery<string | null>({
    queryKey: ['direct-resource-permission', type, resourceId, user?.id],
    enabled: !!user && !!resourceId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from(table)
        .select('role')
        .eq(resourceColumn, resourceId!)
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data?.role ?? null;
    },
  });

  return { role, canView: role === 'viewer' || role === 'editor', canEdit: role === 'editor', isLoading };
};
